import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import {
  CrmPipelineService,
  Pipeline,
} from '../../shared-services/crm-services/crm-object-service/crm-pipeline.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, Observable, take } from 'rxjs';
import { CrmInjectionToken, ObjectType } from '../../tokens-and-interfaces';
import { distinctUntilChanged } from 'rxjs/operators';
import { MatDivider } from '@angular/material/divider';

import { Router } from '@angular/router';
import { PAGE_ROUTES } from '../../constants';

export const SUPPORTED_OBJECT_TYPES = ['Opportunity'];

@Component({
  selector: 'crm-pipeline-selector',
  imports: [CommonModule, ReactiveFormsModule, MatSelectModule, GalaxyFormFieldModule, TranslateModule, MatDivider],
  templateUrl: './pipeline-selector.component.html',
  styleUrl: './pipeline-selector.component.scss',
})
export class PipelineSelectorComponent implements OnInit {
  private readonly config = inject(CrmInjectionToken);

  private selectedPipelineId$$ = new BehaviorSubject<string>('');
  @Input() set selectedPipelineId(id: string) {
    this.selectedPipelineId$$.next(id);
  }
  @Input() objectType: ObjectType = 'Opportunity';
  @Output() selectionChanged = new EventEmitter<Pipeline | null>();
  private lastEmittedPipelineId$$ = new BehaviorSubject<string>('');

  selectedPipelineControl = new FormControl<Pipeline | null>(null);
  protected allPipelines$ = new Observable<Pipeline[]>();

  protected readonly noPipelinesOption = 'NO_PIPELINES';
  constructor(
    private readonly pipelineService: CrmPipelineService,
    private readonly destroyRef: DestroyRef,
    private readonly router: Router,
  ) {}

  ngOnInit(): void {
    if (!SUPPORTED_OBJECT_TYPES.includes(this.objectType)) {
      return;
    }
    this.allPipelines$ = this.pipelineService.listPipelines$(this.objectType);

    this.selectedPipelineControl.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((pipeline) => {
      this.emitPipeline(pipeline);
    });

    combineLatest([this.allPipelines$, this.selectedPipelineId$$])
      .pipe(takeUntilDestroyed(this.destroyRef), distinctUntilChanged())
      .subscribe(([pipelines, selectedPipelineId]) => {
        let selectedPipeline = pipelines?.find((pipeline) => pipeline?.id === selectedPipelineId);
        if (!selectedPipeline) {
          selectedPipeline = pipelines?.[0];
        }
        this.selectedPipelineControl.setValue(selectedPipeline);
      });
  }

  comparePipelines(p1: Pipeline, p2: Pipeline): boolean {
    return p1?.id === p2?.id;
  }

  openPipelineSettings(): void {
    combineLatest([this.config.routePrefix$, this.lastEmittedPipelineId$$])
      .pipe(take(1))
      .subscribe(([routePrefix, lastEmittedPipelineId]) => {
        this.router.navigateByUrl(`${routePrefix}/${PAGE_ROUTES.PIPELINE_SETTINGS.ROOT}/${lastEmittedPipelineId}`);
      });
  }

  private emitPipeline(pipeline: Pipeline | null) {
    this.selectionChanged.emit(pipeline);
    if (pipeline) {
      this.lastEmittedPipelineId$$.next(pipeline.id);
    }
  }
}
